import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  FaBan,
  FaCopy,
  FaEdit,
  FaKey,
  FaMicrochip,
  FaPlus,
  FaRandom,
  FaRedo,
  FaSave,
  FaTrash,
  FaUserCheck,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa";
import { request } from "../../services/api";
import styles from "./UserMenu.module.css";

const emptyForm = {
  username: "",
  password: "",
  name: "",
  family: "",
  phone_num1: "",
  phone_num2: "",
  is_active: true,
};

const permissionKeys = [
  {
    key: "can_monitor",
    label: "مانیتورینگ",
    hint: "مشاهده وضعیت دستگاه",
  },
  {
    key: "can_configure",
    label: "تنظیمات",
    hint: "تغییر نام، visibility، logic، setpoint و تنظیمات",
  },
  {
    key: "can_control_outputs",
    label: "کنترل خروجی",
    hint: "فعال/غیرفعال کردن خروجی‌ها",
  },
  {
    key: "can_manage_users",
    label: "مدیریت کارمندان",
    hint: "تعریف یا ویرایش کارمند برای همین دستگاه",
  },
];

const normalizeBool = (value) => {
  return value === true || value === 1 || value === "1" || value === "true";
};

const StaffManagementModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState("list"); // list | form
  const [formMode, setFormMode] = useState("create"); // create | edit
  const [editingStaff, setEditingStaff] = useState(null);

  const [form, setForm] = useState(emptyForm);
  const [devices, setDevices] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [accessMap, setAccessMap] = useState({});

  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [createdStaff, setCreatedStaff] = useState(null);

  const selectedDevicesCount = useMemo(() => {
    return Object.values(accessMap).filter((item) => item?.selected).length;
  }, [accessMap]);

  useEffect(() => {
    if (!isOpen) return;

    setActiveTab("list");
    setFormMode("create");
    setEditingStaff(null);
    setForm(emptyForm);
    setCreatedStaff(null);
    setMessage("");
    setMessageType("");

    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchInitialData = async () => {
    await fetchManageableDevices();
    await fetchStaffUsers();
  };

  const buildEmptyAccessMap = (deviceList = devices) => {
    const nextAccessMap = {};

    deviceList.forEach((device) => {
      nextAccessMap[String(device.id)] = {
        selected: false,
        can_monitor: true,
        can_configure: false,
        can_control_outputs: false,
        can_manage_users: false,
      };
    });

    return nextAccessMap;
  };

  const fetchManageableDevices = async () => {
    setLoadingDevices(true);
    setMessage("");
    setMessageType("");

    try {
      const json = await request("/get_manageable_devices.php");

      if (!json || json.status !== "success") {
        throw new Error(
          json?.message || "خطا در دریافت دستگاه‌های قابل مدیریت",
        );
      }

      const nextDevices = json.devices || [];
      setDevices(nextDevices);
      setAccessMap(buildEmptyAccessMap(nextDevices));
    } catch (error) {
      console.error("Get manageable devices error:", error);
      setDevices([]);
      setAccessMap({});
      setMessage(error.message || "خطا در دریافت دستگاه‌های قابل مدیریت");
      setMessageType("error");
    } finally {
      setLoadingDevices(false);
    }
  };

  const fetchStaffUsers = async () => {
    setLoadingStaff(true);

    try {
      const json = await request("/list_staff_users.php");

      if (!json || json.status !== "success") {
        throw new Error(json?.message || "خطا در دریافت فهرست کارمندان");
      }

      setStaffUsers(json.staff_users || []);
    } catch (error) {
      console.error("List staff users error:", error);
      setStaffUsers([]);
      setMessage(error.message || "خطا در دریافت فهرست کارمندان");
      setMessageType("error");
    } finally {
      setLoadingStaff(false);
    }
  };

  const refreshAll = async () => {
    setMessage("");
    setMessageType("");
    await fetchInitialData();
  };

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));

    setMessage("");
    setCreatedStaff(null);
  };

  const generateUsernameFromPhone = () => {
    const phone = form.phone_num1.trim();

    if (!phone) {
      setMessage("برای ساخت نام کاربری، ابتدا شماره موبایل اصلی را وارد کنید.");
      setMessageType("error");
      return;
    }

    const cleanPhone = phone.replace(/\D/g, "");
    updateField("username", cleanPhone);
  };

  const generatePassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789@#$";
    let pass = "";

    for (let i = 0; i < 10; i++) {
      pass += chars[Math.floor(Math.random() * chars.length)];
    }

    updateField("password", pass);
  };

  const getDeviceMaxPermission = (device, key) => {
    return normalizeBool(device?.permissions?.[key]);
  };

  const handleToggleDevice = (deviceId) => {
    const id = String(deviceId);

    setAccessMap((prev) => {
      const current = prev[id] || {};
      const nextSelected = !current.selected;

      return {
        ...prev,
        [id]: {
          ...current,
          selected: nextSelected,
          can_monitor: nextSelected,
          can_configure: nextSelected ? current.can_configure : false,
          can_control_outputs: nextSelected
            ? current.can_control_outputs
            : false,
          can_manage_users: nextSelected ? current.can_manage_users : false,
        },
      };
    });

    setMessage("");
    setCreatedStaff(null);
  };

  const handleTogglePermission = (device, key) => {
    const id = String(device.id);

    if (!getDeviceMaxPermission(device, key)) return;

    setAccessMap((prev) => {
      const current = prev[id] || {};

      if (!current.selected) {
        return {
          ...prev,
          [id]: {
            ...current,
            selected: true,
            can_monitor: true,
            [key]: key === "can_monitor" ? true : !current[key],
          },
        };
      }

      if (key === "can_monitor") {
        return {
          ...prev,
          [id]: {
            ...current,
            selected: false,
            can_monitor: false,
            can_configure: false,
            can_control_outputs: false,
            can_manage_users: false,
          },
        };
      }

      return {
        ...prev,
        [id]: {
          ...current,
          can_monitor: true,
          [key]: !current[key],
        },
      };
    });

    setMessage("");
    setCreatedStaff(null);
  };

  const validateForm = () => {
    if (!form.username.trim()) return "نام کاربری الزامی است.";
    if (formMode === "create" && !form.password.trim())
      return "رمز عبور الزامی است.";
    if (form.password.trim() && form.password.trim().length < 6) {
      return "رمز عبور باید حداقل ۶ کاراکتر باشد.";
    }
    if (!form.phone_num1.trim()) return "شماره موبایل اصلی الزامی است.";
    if (selectedDevicesCount === 0)
      return "حداقل یک دستگاه باید برای کارمند انتخاب شود.";

    return "";
  };

  const buildAccessPayload = () => {
    return devices
      .filter((device) => accessMap[String(device.id)]?.selected)
      .map((device) => {
        const item = accessMap[String(device.id)];

        return {
          device_id: device.id,
          can_monitor: true,
          can_configure: !!item.can_configure,
          can_control_outputs: !!item.can_control_outputs,
          can_manage_users: !!item.can_manage_users,
        };
      });
  };

  const buildCreatePayload = () => ({
    username: form.username.trim(),
    password: form.password.trim(),
    name: form.name.trim(),
    family: form.family.trim(),
    phone_num1: form.phone_num1.trim(),
    phone_num2: form.phone_num2.trim(),
    devices: buildAccessPayload(),
  });

  const buildUpdatePayload = () => {
    const payload = {
      staff_id: editingStaff?.id,
      username: form.username.trim(),
      name: form.name.trim(),
      family: form.family.trim(),
      phone_num1: form.phone_num1.trim(),
      phone_num2: form.phone_num2.trim(),
      is_active: !!form.is_active,
      devices: buildAccessPayload(),
    };

    if (form.password.trim()) {
      payload.password = form.password.trim();
    }

    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateForm();

    if (validationError) {
      setMessage(validationError);
      setMessageType("error");
      return;
    }

    setSubmitting(true);
    setMessage("");
    setMessageType("");
    setCreatedStaff(null);

    try {
      const endpoint =
        formMode === "edit"
          ? "/update_staff_user.php"
          : "/create_staff_user.php";
      const payload =
        formMode === "edit" ? buildUpdatePayload() : buildCreatePayload();

      const json = await request(endpoint, "POST", payload);

      if (!json || json.status !== "success") {
        throw new Error(json?.message || "خطا در ثبت اطلاعات کارمند");
      }

      if (formMode === "create") {
        setCreatedStaff(json);
        setMessage(json.message || "کارمند جدید با موفقیت تعریف شد.");
        setMessageType("success");
        await fetchStaffUsers();
      } else {
        setMessage(json.message || "اطلاعات کارمند با موفقیت ویرایش شد.");
        setMessageType("success");
        await fetchStaffUsers();
        setActiveTab("list");
        setEditingStaff(null);
      }
    } catch (error) {
      console.error("Save staff user error:", error);
      setMessage(error.message || "خطا در ثبت اطلاعات کارمند");
      setMessageType("error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetForm = () => {
    setForm(emptyForm);
    setEditingStaff(null);
    setFormMode("create");
    setCreatedStaff(null);
    setMessage("");
    setMessageType("");
    setAccessMap(buildEmptyAccessMap());
  };

  const openCreateForm = () => {
    handleResetForm();
    setFormMode("create");
    setActiveTab("form");
  };

  const openEditForm = (staff) => {
    const nextAccessMap = buildEmptyAccessMap();

    (staff.access || []).forEach((access) => {
      const id = String(access.device_id);

      if (!nextAccessMap[id]) return;
      if (!normalizeBool(access.is_active)) return;

      nextAccessMap[id] = {
        selected: true,
        can_monitor: normalizeBool(access.can_monitor),
        can_configure: normalizeBool(access.can_configure),
        can_control_outputs: normalizeBool(access.can_control_outputs),
        can_manage_users: normalizeBool(access.can_manage_users),
      };
    });

    setEditingStaff(staff);
    setFormMode("edit");
    setActiveTab("form");
    setCreatedStaff(null);
    setMessage("");
    setMessageType("");
    setAccessMap(nextAccessMap);
    setForm({
      username: staff.username || "",
      password: "",
      name: staff.name || "",
      family: staff.family || "",
      phone_num1: staff.phone_num1 || "",
      phone_num2: staff.phone_num2 || "",
      is_active: normalizeBool(staff.is_active),
    });
  };

  const handleToggleStaffActive = async (staff) => {
    const nextActive = !normalizeBool(staff.is_active);
    const actionText = nextActive ? "فعال" : "غیرفعال";

    const confirmed = window.confirm(
      `آیا از ${actionText} کردن این کارمند مطمئن هستید؟`,
    );
    if (!confirmed) return;

    setActionLoadingId(staff.id);
    setMessage("");
    setMessageType("");

    try {
      const json = await request("/set_staff_user_status.php", "POST", {
        staff_id: staff.id,
        is_active: nextActive,
      });

      if (!json || json.status !== "success") {
        throw new Error(json?.message || "خطا در تغییر وضعیت کارمند");
      }

      setMessage(json.message || `کارمند با موفقیت ${actionText} شد.`);
      setMessageType("success");
      await fetchStaffUsers();
    } catch (error) {
      console.error("Toggle staff active error:", error);
      setMessage(error.message || "خطا در تغییر وضعیت کارمند");
      setMessageType("error");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDeleteStaff = async (staff) => {
    const displayName =
      `${staff.name || ""} ${staff.family || ""}`.trim() || staff.username;
    const confirmed = window.confirm(
      `حذف کارمند «${displayName}» برگشت‌پذیر نیست. آیا مطمئن هستید؟`,
    );

    if (!confirmed) return;

    setActionLoadingId(staff.id);
    setMessage("");
    setMessageType("");

    try {
      const json = await request("/delete_staff_user.php", "POST", {
        staff_id: staff.id,
      });

      if (!json || json.status !== "success") {
        throw new Error(json?.message || "خطا در حذف کارمند");
      }

      setMessage(json.message || "کارمند با موفقیت حذف شد.");
      setMessageType("success");
      await fetchStaffUsers();
    } catch (error) {
      console.error("Delete staff error:", error);
      setMessage(error.message || "خطا در حذف کارمند");
      setMessageType("error");
    } finally {
      setActionLoadingId(null);
    }
  };

  const buildStaffMessage = () => {
    if (!createdStaff?.user) return "";

    return [
      "اطلاعات ورود کارمند به پنل هوشمند گلخانه:",
      `نام کاربری: ${createdStaff.user.username}`,
      `رمز عبور: ${createdStaff.user.plain_password}`,
      "آدرس پنل: https://sfarm.tabangohar.com",
    ].join("\n");
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert("کپی شد");
    } catch {
      alert("امکان کپی وجود ندارد");
    }
  };

  const renderAccessBadges = (staff) => {
    const activeAccess = (staff.access || []).filter((item) =>
      normalizeBool(item.is_active),
    );

    if (activeAccess.length === 0) {
      return <span className={styles.staffTinyMuted}>بدون دسترسی فعال</span>;
    }

    return (
      <div className={styles.staffAccessBadgeList}>
        {activeAccess.map((access) => (
          <span key={access.device_id} className={styles.staffAccessBadge}>
            {access.device_name || `دستگاه ${access.device_id}`}
          </span>
        ))}
      </div>
    );
  };

  const renderStaffList = () => {
    if (loadingStaff) {
      return (
        <div className={styles.staffStateBox}>
          در حال دریافت فهرست کارمندان...
        </div>
      );
    }

    if (staffUsers.length === 0) {
      return (
        <div className={styles.staffStateBox}>
          هنوز کارمندی برای دستگاه‌های قابل مدیریت شما تعریف نشده است.
        </div>
      );
    }

    return (
      <div className={styles.staffUsersList}>
        {staffUsers.map((staff) => {
          const isActive = normalizeBool(staff.is_active);
          const displayName =
            `${staff.name || ""} ${staff.family || ""}`.trim() || "بدون نام";

          return (
            <div key={staff.id} className={styles.staffUserCard}>
              <div className={styles.staffUserMainInfo}>
                <div className={styles.staffUserAvatar}>
                  <FaUserCheck />
                </div>

                <div className={styles.staffUserTextBox}>
                  <div className={styles.staffUserNameRow}>
                    <strong>{displayName}</strong>
                    <span
                      className={
                        isActive
                          ? styles.staffStatusActive
                          : styles.staffStatusInactive
                      }
                    >
                      {isActive ? "فعال" : "غیرفعال"}
                    </span>
                  </div>

                  <div className={styles.staffUserSubInfo}>
                    <span>{staff.username}</span>
                    <span>{staff.phone_num1 || "-"}</span>
                  </div>

                  {renderAccessBadges(staff)}
                </div>
              </div>

              <div className={styles.staffUserActions}>
                <button
                  type="button"
                  className={styles.staffIconActionBtn}
                  onClick={() => openEditForm(staff)}
                  disabled={actionLoadingId === staff.id}
                  title="ویرایش"
                >
                  <FaEdit />
                  <span>ویرایش</span>
                </button>

                <button
                  type="button"
                  className={styles.staffIconActionBtn}
                  onClick={() => handleToggleStaffActive(staff)}
                  disabled={actionLoadingId === staff.id}
                  title={isActive ? "غیرفعال کردن" : "فعال کردن"}
                >
                  <FaBan />
                  <span>{isActive ? "غیرفعال" : "فعال"}</span>
                </button>

                <button
                  type="button"
                  className={`${styles.staffIconActionBtn} ${styles.staffDangerBtn}`}
                  onClick={() => handleDeleteStaff(staff)}
                  disabled={actionLoadingId === staff.id}
                  title="حذف"
                >
                  <FaTrash />
                  <span>حذف</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderAccessForm = () => {
    if (loadingDevices) {
      return (
        <div className={styles.staffStateBox}>
          در حال دریافت دستگاه‌های قابل مدیریت...
        </div>
      );
    }

    if (devices.length === 0) {
      return (
        <div className={styles.staffStateBox}>
          دستگاه قابل مدیریتی برای تعریف یا ویرایش کارمند وجود ندارد.
        </div>
      );
    }

    return (
      <div className={styles.staffDevicesList}>
        {devices.map((device) => {
          const id = String(device.id);
          const item = accessMap[id] || {};
          const isSelected = !!item.selected;

          return (
            <div
              key={device.id}
              className={`
                ${styles.staffDeviceCard}
                ${isSelected ? styles.staffDeviceCardActive : ""}
              `}
            >
              <div className={styles.staffDeviceHeader}>
                <label className={styles.staffDeviceSelect}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleToggleDevice(device.id)}
                  />
                  <span>انتخاب دستگاه</span>
                </label>

                <div className={styles.staffDeviceTitleBox}>
                  <div className={styles.staffDeviceName}>
                    {device.device_name || `دستگاه ${device.id}`}
                  </div>
                  <div className={styles.staffDeviceSerial}>
                    {device.device_serial}
                  </div>
                </div>
              </div>

              <div className={styles.staffPermissionGrid}>
                {permissionKeys.map((permission) => {
                  const isAllowed = getDeviceMaxPermission(
                    device,
                    permission.key,
                  );
                  const checked =
                    permission.key === "can_monitor"
                      ? isSelected
                      : !!item[permission.key];

                  return (
                    <label
                      key={permission.key}
                      className={`
                        ${styles.staffPermissionItem}
                        ${!isAllowed || !isSelected ? styles.staffPermissionDisabled : ""}
                      `}
                      title={
                        isAllowed
                          ? permission.hint
                          : "شما اجازه اعطای این دسترسی را ندارید"
                      }
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={
                          !isAllowed ||
                          (!isSelected && permission.key !== "can_monitor")
                        }
                        onChange={() =>
                          handleTogglePermission(device, permission.key)
                        }
                      />
                      <span>{permission.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className={`${styles.userInfoModalOverlay} ${styles.staffManagementOverlay}`}
    >
      <div
        className={`
          ${styles.userInfoModalBox}
          ${styles.staffManagementModalBox}
        `}
      >
        <div className={styles.userInfoModalHeader}>
          <div className={styles.userInfoModalTitleBox}>
            <FaUsers className={styles.userInfoModalIcon} />

            <div>
              <div className={styles.userInfoModalTitle}>مدیریت کارمندان</div>
              <div className={styles.userInfoModalSubTitle}>
                مشاهده، تعریف، ویرایش و کنترل دسترسی کارمندان روی دستگاه‌های
                مجاز
              </div>
            </div>
          </div>

          <button
            type="button"
            className={styles.userInfoModalClose}
            onClick={onClose}
            aria-label="بستن"
          >
            ×
          </button>
        </div>

        <div className={styles.staffModalTabs}>
          <button
            type="button"
            className={`${styles.staffTabBtn} ${activeTab === "list" ? styles.staffTabBtnActive : ""}`}
            onClick={() => {
              setActiveTab("list");
              setFormMode("create");
              setEditingStaff(null);
              setCreatedStaff(null);
            }}
          >
            <FaUsers />
            فهرست کارمندان
          </button>

          <button
            type="button"
            className={`${styles.staffTabBtn} ${activeTab === "form" && formMode === "create" ? styles.staffTabBtnActive : ""}`}
            onClick={openCreateForm}
          >
            <FaPlus />
            تعریف کارمند جدید
          </button>

          <button
            type="button"
            className={styles.staffRefreshBtn}
            onClick={refreshAll}
            disabled={loadingStaff || loadingDevices}
          >
            <FaRedo />
            بروزرسانی
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div
            className={`${styles.userInfoModalBody} ${styles.staffManagementScrollBody}`}
          >
            <div className={styles.staffManagementBody}>
              {message && (
                <div
                  className={`
                    ${styles.staffMessage}
                    ${messageType === "success" ? styles.staffMessageSuccess : ""}
                    ${messageType === "error" ? styles.staffMessageError : ""}
                  `}
                >
                  {message}
                </div>
              )}

              {activeTab === "list" && (
                <>
                  <div className={styles.staffSectionTitle}>
                    <FaUsers />
                    <span>کارمندانی که به دستگاه‌های شما دسترسی دارند</span>
                  </div>

                  {renderStaffList()}
                </>
              )}

              {activeTab === "form" && (
                <>
                  <div className={styles.staffSectionTitle}>
                    <FaUserPlus />
                    <span>
                      {formMode === "edit"
                        ? "ویرایش کارمند"
                        : "اطلاعات کارمند جدید"}
                    </span>
                  </div>

                  {formMode === "edit" && editingStaff && (
                    <div className={styles.staffEditNotice}>
                      در حال ویرایش کارمند:{" "}
                      <strong>{editingStaff.username}</strong>
                    </div>
                  )}

                  <div className={styles.staffManagementGrid}>
                    <div className={styles.staffInputRow}>
                      <label className={styles.staffInputLabel}>
                        شماره موبایل اصلی *
                      </label>
                      <input
                        className={styles.staffInput}
                        value={form.phone_num1}
                        onChange={(event) =>
                          updateField("phone_num1", event.target.value)
                        }
                        placeholder="مثلاً 09123456789"
                      />
                    </div>

                    <div className={styles.staffInputRow}>
                      <label className={styles.staffInputLabel}>
                        نام کاربری *
                      </label>
                      <div className={styles.staffInlineInput}>
                        <input
                          className={styles.staffInput}
                          value={form.username}
                          onChange={(event) =>
                            updateField("username", event.target.value)
                          }
                          placeholder="نام کاربری"
                        />

                        <button
                          type="button"
                          className={styles.staffSmallBtn}
                          onClick={generateUsernameFromPhone}
                          title="ساخت از شماره موبایل"
                        >
                          <FaRandom />
                        </button>
                      </div>
                    </div>

                    <div className={styles.staffInputRow}>
                      <label className={styles.staffInputLabel}>
                        {formMode === "edit"
                          ? "رمز عبور جدید"
                          : "رمز عبور اولیه *"}
                      </label>
                      <div className={styles.staffInlineInput}>
                        <input
                          className={styles.staffInput}
                          value={form.password}
                          onChange={(event) =>
                            updateField("password", event.target.value)
                          }
                          placeholder={
                            formMode === "edit"
                              ? "برای عدم تغییر خالی بماند"
                              : "رمز اولیه"
                          }
                        />

                        <button
                          type="button"
                          className={styles.staffSmallBtn}
                          onClick={generatePassword}
                        >
                          تولید
                        </button>
                      </div>
                    </div>

                    <div className={styles.staffInputRow}>
                      <label className={styles.staffInputLabel}>
                        شماره موبایل دوم
                      </label>
                      <input
                        className={styles.staffInput}
                        value={form.phone_num2}
                        onChange={(event) =>
                          updateField("phone_num2", event.target.value)
                        }
                        placeholder="اختیاری"
                      />
                    </div>

                    <div className={styles.staffInputRow}>
                      <label className={styles.staffInputLabel}>نام</label>
                      <input
                        className={styles.staffInput}
                        value={form.name}
                        onChange={(event) =>
                          updateField("name", event.target.value)
                        }
                        placeholder="نام کارمند"
                      />
                    </div>

                    <div className={styles.staffInputRow}>
                      <label className={styles.staffInputLabel}>
                        نام خانوادگی
                      </label>
                      <input
                        className={styles.staffInput}
                        value={form.family}
                        onChange={(event) =>
                          updateField("family", event.target.value)
                        }
                        placeholder="نام خانوادگی"
                      />
                    </div>
                  </div>

                  {formMode === "edit" && (
                    <label className={styles.staffActiveToggleBox}>
                      <input
                        type="checkbox"
                        checked={!!form.is_active}
                        onChange={(event) =>
                          updateField("is_active", event.target.checked)
                        }
                      />
                      <span>حساب کارمند فعال باشد</span>
                    </label>
                  )}

                  <div className={styles.staffSectionTitle}>
                    <FaMicrochip />
                    <span>دستگاه‌ها و سطح دسترسی</span>
                  </div>

                  {renderAccessForm()}

                  {createdStaff?.user && (
                    <div className={styles.staffCredentialBox}>
                      <div className={styles.staffCredentialTitle}>
                        <FaKey />
                        <span>اطلاعات ورود کارمند</span>
                      </div>

                      <div className={styles.staffCredentialRow}>
                        <span>نام کاربری:</span>
                        <strong>{createdStaff.user.username}</strong>
                      </div>

                      <div className={styles.staffCredentialRow}>
                        <span>رمز اولیه:</span>
                        <strong>{createdStaff.user.plain_password}</strong>
                      </div>

                      <button
                        type="button"
                        className={styles.staffCopyBtn}
                        onClick={() => copyText(buildStaffMessage())}
                      >
                        <FaCopy />
                        کپی متن ارسال به کارمند
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className={styles.userInfoModalFooter}>
            {activeTab === "list" ? (
              <>
                <button
                  type="button"
                  className={styles.userInfoSecondaryBtn}
                  onClick={onClose}
                >
                  بستن
                </button>

                <button
                  type="button"
                  className={styles.userInfoPrimaryBtn}
                  onClick={openCreateForm}
                >
                  <FaPlus />
                  تعریف کارمند جدید
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className={styles.userInfoSecondaryBtn}
                  onClick={() => {
                    setActiveTab("list");
                    setFormMode("create");
                    setEditingStaff(null);
                    setCreatedStaff(null);
                    setMessage("");
                    setMessageType("");
                  }}
                  disabled={submitting}
                >
                  بازگشت به فهرست
                </button>

                <button
                  type="button"
                  className={styles.userInfoSecondaryBtn}
                  onClick={handleResetForm}
                  disabled={submitting}
                >
                  فرم جدید
                </button>

                <button
                  type="submit"
                  className={styles.userInfoPrimaryBtn}
                  disabled={
                    submitting || loadingDevices || devices.length === 0
                  }
                >
                  <FaSave />
                  {submitting
                    ? "در حال ثبت..."
                    : formMode === "edit"
                      ? "ذخیره تغییرات"
                      : "ثبت کارمند"}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};

export default StaffManagementModal;
